import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { Link } from 'react-router-dom';

import type { RecommendationSource } from '~/api/Catalog';
import ChevronRightIcon from '~/components/MarketplaceIcons/ChevronRightIcon';
import { ProductCardCompact } from '~/components/ProductCardCompact';
import { ProductListAnalyticsProvider } from '~/components/ProductListAnalytics';
import { recommendationsProductList } from '~/services/analytics/productLists';
import { catalogStore } from '~/stores';

import {
  INLINE_LINK_CLASS,
  PRODUCT_ROW_CLASS,
  PRODUCT_ROW_MAX_CARDS,
  SECTION_TITLE_CLASS,
} from '../constants';

interface Props {
  source: RecommendationSource;
  title: string;
  /** Target of the "see all" link in the section header. */
  seeAllLink?: string;
  isLoading?: boolean;
}

/**
 * Recommendation row of the DubaiExpress product page with the mockup's card:
 * one full row on desktop, as many cards as the width fits.
 */
const ProductDERecommendationsView = ({
  source,
  title,
  seeAllLink,
  isLoading,
}: Props) => {
  const { t } = useTranslation();
  const products = catalogStore.productRecommendations[source];

  if (catalogStore.productRecommendationsMeta.isLoading || isLoading) {
    return (
      <section className="mt-8">
        <div className="mb-2.5 flex items-baseline justify-between">
          <h2 className={SECTION_TITLE_CLASS}>{title}</h2>
        </div>
        <div className={PRODUCT_ROW_CLASS}>
          {Array.from(
            { length: PRODUCT_ROW_MAX_CARDS },
            (_, i) => `s-${i}`,
          ).map((key) => (
            <Skeleton
              key={key}
              height={299}
              containerClassName="block"
              className="rounded-[16px]!"
            />
          ))}
        </div>
      </section>
    );
  }

  if (!products?.data.length) {
    return null;
  }

  return (
    <ProductListAnalyticsProvider
      list={recommendationsProductList(source, title)}
    >
      <section className="mt-8">
        <div className="mb-2.5 flex items-baseline justify-between">
          <h2 className={SECTION_TITLE_CLASS}>{title}</h2>
          {seeAllLink && (
            <Link to={seeAllLink} className={INLINE_LINK_CLASS}>
              {t('seeAll')}
              <ChevronRightIcon className="size-3.5" />
            </Link>
          )}
        </div>
        <div className={PRODUCT_ROW_CLASS}>
          {products.data.map((product, index) => (
            <ProductCardCompact
              key={product.id}
              product={product}
              index={index}
            />
          ))}
        </div>
      </section>
    </ProductListAnalyticsProvider>
  );
};

export const ProductDERecommendations = observer(ProductDERecommendationsView);
